/**
 * PostHog product analytics for `@shopista/web` (T50).
 *
 * Gated on the build-time `VITE_POSTHOG_KEY` env var, mirroring
 * [sentry.ts](./sentry.ts): Vite bakes env vars into the bundle at build
 * time (runtime env doesn't flow). When the key is unset (local dev) this
 * module is a TRUE no-op — no `posthog.init`, no network, no console output,
 * no SDK side effects beyond loading. Every export below short-circuits when
 * the key is unset or `init` never ran, so callers never need to guard.
 *
 * The same web bundle runs inside the Android Capacitor WebView, so this one
 * SDK captures both platforms; the `platform` super-property (set from
 * `Capacitor.getPlatform()`, passed in by `main.tsx`) distinguishes them — the
 * WebView user-agent can't.
 *
 * `api_host` points at a first-party reverse proxy (`VITE_POSTHOG_HOST`) so an
 * ad blocker can't drop ingestion; `ui_host` is the real PostHog app URL so the
 * in-product links still resolve. `person_profiles: 'always'` makes anonymous
 * visitors profiles (the acquisition funnel + remarketing need them), and the
 * cross-subdomain cookie carries the anonymous distinct_id from `shopista.ca`
 * to `app.shopista.ca` so an ad→signup journey stitches into one identity.
 *
 * Sensitive data (passwords, allergy/health, dietary exclusions, precise
 * location/address) is masked at the SDK — it never serializes into any
 * captured payload. See `SENSITIVE_PROPERTY_KEYS` + `sanitizeCaptureResult`.
 */

import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

// `Properties` isn't re-exported from the `posthog-js` top-level module, so
// derive it from `CaptureResult` (its `properties` field IS the SDK's
// `Properties` type) rather than reaching into the transitive `@posthog/types`.
type Properties = CaptureResult['properties'];

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

/**
 * The three values `Capacitor.getPlatform()` returns: `android` / `ios`
 * (installed Capacitor WebView) vs `web` (browser). Sourced from
 * `Capacitor.getPlatform()` — NOT the boolean `isNativePlatform()` — so the
 * super-property distinguishes the two native platforms, not just native-vs-web.
 */
export type AnalyticsPlatform = 'android' | 'ios' | 'web';

export interface InitAnalyticsOptions {
  /**
   * Platform value from `Capacitor.getPlatform()` — registered as a
   * super-property so EVERY event carries one of `android` / `ios` / `web`.
   * `main.tsx` passes `resolvePlatform()` (the shared `getPlatform()` reader).
   */
  platform: AnalyticsPlatform;
}

/**
 * Property keys whose VALUES must never leave the device. Passwords, health /
 * allergy data, dietary exclusions, and precise location/address. The check is
 * a case-insensitive substring match against these fragments, so `password`
 * catches `newPassword`/`password_confirm`, `health` catches `healthCondition`, etc.
 *
 * This is the denylist behind both guards:
 *   1. `property_denylist` — PostHog drops these top-level keys pre-send.
 *   2. `sanitizeCaptureResult` (via `before_send`) — a recursive strip that also
 *      reaches keys nested inside `$set` / `$set_once` / any object property,
 *      which `property_denylist` (top-level only) would miss.
 */
export const SENSITIVE_PROPERTY_KEYS = [
  'password',
  'health',
  'dietary',
  'exclusion', // dietary exclusions
  'address',
  'postal', // postal code — precise location
  'postcode',
  'latitude',
  'longitude',
  'geolocation',
  'coordinates',
] as const;

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_PROPERTY_KEYS.some((fragment) => lower.includes(fragment));
}

/**
 * Strip a single value: recurse into plain objects (drop sensitive keys) and
 * arrays (walk every element, including nested arrays), and pass primitives
 * through untouched. Returns a NEW value for objects/arrays — the input is never
 * mutated. This is what lets a sensitive key hidden inside an array-of-objects
 * under a non-sensitive key (e.g. `{ items: [{ address: '…' }] }`) still be
 * stripped: `stripSensitiveProperties` handles the key check, this handles the
 * value walk regardless of container shape.
 */
function stripSensitiveValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripSensitiveValue);
  }
  if (value && typeof value === 'object') {
    return stripSensitiveProperties(value as Properties);
  }
  return value;
}

/**
 * Recursively remove every sensitive key from a properties object. Walks nested
 * objects (e.g. `$set`, `$set_once`) and arrays so a sensitive value can't hide
 * one level down — including a sensitive key nested inside an array of objects
 * under a non-sensitive key. Returns a new object; does not mutate the input.
 */
export function stripSensitiveProperties(props: Properties): Properties {
  const out: Properties = {};
  for (const [key, value] of Object.entries(props)) {
    if (isSensitiveKey(key)) continue;
    out[key] = stripSensitiveValue(value);
  }
  return out;
}

/**
 * `before_send` hook — the last gate before an event is queued for delivery.
 * Strips sensitive keys from the event's properties (top-level AND nested).
 * Returning the (sanitized) result keeps the event; returning `null` would
 * drop it — we never drop, only sanitize.
 */
export function sanitizeCaptureResult(
  cr: CaptureResult | null,
): CaptureResult | null {
  if (!cr) return cr;
  if (cr.properties) {
    cr.properties = stripSensitiveProperties(cr.properties);
  }
  return cr;
}

/** True once `posthog.init` has run (i.e. the key was set). */
let initialized = false;

/**
 * Initialize analytics. No-op when `VITE_POSTHOG_KEY` is unset. Calling it a
 * second time after a successful init does nothing.
 */
export function initAnalytics(opts: InitAnalyticsOptions): void {
  if (!KEY || initialized) return;

  // Fail closed: the first-party proxy is REQUIRED. If the key is set but the
  // host is not, refuse to init rather than let PostHog fall back to its own
  // `us.i.posthog.com` default — that would bypass the proxy and send ingestion
  // straight to a posthog.com domain (an ad blocker could drop it, and it
  // violates the first-party-only contract). Analytics stays off instead.
  if (!HOST) {
    console.warn(
      '[analytics] VITE_POSTHOG_KEY is set but VITE_POSTHOG_HOST is not — ' +
        'refusing to init to avoid sending to a non-first-party host. ' +
        'Set VITE_POSTHOG_HOST to the /ph proxy path.',
    );
    return;
  }

  posthog.init(KEY, {
    // First-party reverse proxy (VITE_POSTHOG_HOST) so ad blockers can't drop
    // ingestion. `ui_host` stays the real PostHog app URL for in-product links.
    // The `!HOST` guard above narrows HOST to `string` here, so api_host is
    // always a defined string — never `undefined` (exactOptionalPropertyTypes-clean).
    api_host: HOST,
    ui_host: 'https://us.i.posthog.com',
    // The cross-subdomain cookie carries the anonymous distinct_id across
    // shopista.ca → app.shopista.ca; localStorage keeps the offline-safe queue.
    persistence: 'localStorage+cookie',
    // Set the cookie on the top-level `.shopista.ca` domain so the marketing
    // site and the app share one anonymous identity (the funnel stitch).
    cross_subdomain_cookie: true,
    // Anonymous visitors become profiles — required for the pre-signup funnel
    // and the remarketing audiences (identified_only would drop them).
    person_profiles: 'always',
    // Sensitive-input masking (Deliverable 3):
    //  - property_denylist drops these top-level property keys pre-send.
    //  - before_send (sanitizeCaptureResult) additionally strips nested keys.
    //  - mask_all_element_attributes stops autocapture from reading input
    //    attribute values (a masked <input> never leaks its typed value); the
    //    default `.ph-no-capture` / `[data-ph-no-capture]` opt-out still holds.
    property_denylist: [...SENSITIVE_PROPERTY_KEYS],
    before_send: sanitizeCaptureResult,
    mask_all_element_attributes: true,
    // Capture from first visit (anonymous funnel). Autocapture stays on but is
    // attribute-masked above; pageviews are captured by default.
    autocapture: true,
  });

  initialized = true;

  // Persistent super-properties: registered once so EVERY event carries them.
  // `platform` distinguishes Android (WebView) from web; `build` + `channel`
  // + `environment` come from the baked build stamp (mirrors sentry.ts). The
  // channel is the build channel (`dev`/`production`/`local`), NOT Vite's MODE
  // (which is `production` for any optimized build, including the dev image).
  posthog.register({
    platform: opts.platform,
    build: __APP_BUILD__?.build,
    channel: __APP_BUILD__?.channel || 'local',
    environment: __APP_BUILD__?.channel || 'local',
  });
}

/** Capture a product event. Safe no-op when analytics isn't initialized. */
export function captureEvent(name: string, props?: Properties): void {
  if (!initialized) return;
  posthog.capture(name, props);
}

/** Device-location measurement payload. Coordinates are intentionally absent. */
export interface LocationOutcomeAnalytics {
  surface: 'settings' | 'mode-detection';
  platform: AnalyticsPlatform;
  outcome: 'ok' | 'denied' | 'unavailable' | 'timeout' | 'unsupported';
  elapsed_ms: number;
  accuracy_m: number | null;
  from_cache: boolean;
  retry_count: number;
}

/** Capture a device-location outcome without accepting coordinate fields. */
export function captureLocationOutcome(props: LocationOutcomeAnalytics): void {
  captureEvent('device_location_outcome', props);
}

/**
 * Identify the current visitor with the CANONICAL backend user id (the same id
 * on web + Android → one identity). Also merges the anonymous pre-signup
 * history into the identified profile (PostHog's identify semantics). Safe
 * no-op when analytics isn't initialized.
 */
export function identifyUser(userId: string, props?: Properties): void {
  if (!initialized) return;
  posthog.identify(userId, props);
}

/**
 * Reset the SDK's identity + anonymous distinct_id — called on sign-out
 * (`SESSION_CLEARED_EVENT`) so the next user on the device starts a fresh
 * anonymous identity, not the previous person's. Safe no-op when analytics
 * isn't initialized.
 */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}
